import { useMemo, useCallback } from 'react';
import { useSelector } from "react-redux";
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from "../../../../store";
import { MultiSelectOption } from "../../../../components/Selectors/SimplifiedMultiSelect";
import { BackendTables, COUNTERPART_TO_HUMAN_READABLE, Counterpart, PLATFORM_TO_HUMAN_READABLE, Platform, STAGE, StageToHumanReadable, CustomMetricValueType, CustomMetricSource, AddressType, PROSPECT_PHONE_TYPE, PHONE_TYPE_TO_HUMAN_READABLE, UNKNOWN_PHONE_TYPE } from "../../../../interfaces/db";
import { SESSION_METRICS_COLUMNS, PROSPECT_INFO_COLUMNS, CustomMetricColumns, SessionV2Columns } from "../../../../interfaces/table";
import { BackendColumns } from "../../../../interfaces/table";
import { ProspectInfoChoices } from "interfaces/metadata";
import { getCustomCode } from "../../../../components/CustomScores/cfg";
import { convertFromReduxSafeCustomDefinitions } from "../../users/slice";
import { useUserData } from "../../users/user-data-hook";

interface UseColumnOptionsReturn {
  getColumnOptions: (column: BackendColumns) => MultiSelectOption[];
  hasOptions: (column: BackendColumns) => boolean;
}

// Memoized selectors to prevent unnecessary re-renders
const selectProspectInfoOptions = createSelector(
  (state: RootState) => state.prospectInfoOptions.value,
  (value) => value ?? null
);

const selectDispositionOptions = createSelector(
  (state: RootState) => state.dispositionOptions.value,
  (value) => value ?? []
);

export const useColumnOptions = (): UseColumnOptionsReturn => {
  const { team, customDefinitions: reduxSafeDefinitions } = useUserData();
  const prospectInfoOptions = useSelector(selectProspectInfoOptions);
  const dispositionInfoOptions = useSelector(selectDispositionOptions);
  const { routes } = useSelector((state: RootState) => state.inboundRoutes);
  const teamId = team?.team_id || ''
  
  // Stabilize customDefinitions to prevent unnecessary re-renders
  const customDefinitions = useMemo(() => {
    const safeDefinitions = reduxSafeDefinitions ?? [];
    return safeDefinitions.length > 0 ? convertFromReduxSafeCustomDefinitions(safeDefinitions) : [];
  }, [reduxSafeDefinitions])
  
  // Stabilize codeOptions to prevent unnecessary re-renders
  const codeOptions = useMemo(() => {
    return getCustomCode(teamId) || [];
  }, [teamId])

  const getProspectChoicesForColumn = useCallback((column: BackendColumns, choices: ProspectInfoChoices | null): MultiSelectOption[] => {
    if (!choices) return [];
    
    const matching_filters = choices.filter((x) => x.field_name === column).map((v) => v.choices).flat();
    if (matching_filters.length > 0) {
      return matching_filters.map((v) => ({ 
        label: v, 
        value: v, 
        column: { column_name: column, table: BackendTables.PROSPECT_INFO }
      }));
    }
    return [];
  }, []);

  // Helper function to check if a column is a custom metric
  const isCustomMetricColumn = (columnName: string): boolean => {
    return Object.values(CustomMetricColumns).includes(columnName as CustomMetricColumns) && 
           columnName !== CustomMetricColumns.SESSION_ID && 
           !columnName.startsWith('text_');
  };

  // Helper function to get custom metric definition
  const getCustomMetricDefinition = useCallback((columnName: string) => {
    if (!isCustomMetricColumn(columnName)) return null;
    
    // Extract metric index from column name (e.g., "metric_0" -> 0)
    const match = columnName.match(/^metric_(\d+)$/);
    if (!match) return null;
    
    const metricIdx = parseInt(match[1], 10);
    return customDefinitions.find(def => def.metric_idx === metricIdx);
  }, [customDefinitions]);

  const getColumnOptions = useMemo(() => (column: BackendColumns): MultiSelectOption[] => {
    switch (column) {
      // Session Metrics Columns
      case SESSION_METRICS_COLUMNS.STAGE:
        return Object.values(STAGE).map((v) => ({ 
          column: { column_name: SESSION_METRICS_COLUMNS.STAGE, table: BackendTables.SESSION_METRICS }, 
          label: StageToHumanReadable[v], 
          value: v 
        }))
      case SESSION_METRICS_COLUMNS.PLATFORM:
        return Object.values(Platform).map((v) => ({ 
          column: { column_name: SESSION_METRICS_COLUMNS.PLATFORM, table: BackendTables.SESSION_METRICS }, 
          label: PLATFORM_TO_HUMAN_READABLE[v], 
          value: v 
        }))
      case SESSION_METRICS_COLUMNS.BEST_COUNTERPART:
        return Object.values(Counterpart).map((v) => ({
          label: COUNTERPART_TO_HUMAN_READABLE[v],
          value: v,
          column: { column_name: SESSION_METRICS_COLUMNS.BEST_COUNTERPART, table: BackendTables.SESSION_METRICS },
        }))
      case SESSION_METRICS_COLUMNS.USER_DISPOSITION:
      case SESSION_METRICS_COLUMNS.USER_PURPOSE:
      case SESSION_METRICS_COLUMNS.USER_SENTIMENT:
      case SESSION_METRICS_COLUMNS.TRELLUS_DISPOSITION:
        return getProspectChoicesForColumn(column, dispositionInfoOptions);
      case PROSPECT_INFO_COLUMNS.PROSPECT_PHONE_TYPE:
        return [
          ...Object.values(PROSPECT_PHONE_TYPE).map((v) => ({ 
            column: { column_name: PROSPECT_INFO_COLUMNS.PROSPECT_PHONE_TYPE, table: BackendTables.PROSPECT_INFO }, 
            label: PHONE_TYPE_TO_HUMAN_READABLE[v], 
            value: v 
          })),
          // Add option for null/undefined phone types
          {
            column: { column_name: PROSPECT_INFO_COLUMNS.PROSPECT_PHONE_TYPE, table: BackendTables.PROSPECT_INFO },
            label: UNKNOWN_PHONE_TYPE,
            value: null as any // Backend expects null for unknown phone types
          }
        ]
      case PROSPECT_INFO_COLUMNS.CADENCE:
      case PROSPECT_INFO_COLUMNS.CADENCE_2:
      case PROSPECT_INFO_COLUMNS.CADENCE_3:
      case PROSPECT_INFO_COLUMNS.CADENCE_STEP:
      case PROSPECT_INFO_COLUMNS.CADENCE_STEP_2:
      case PROSPECT_INFO_COLUMNS.CADENCE_STEP_3:
      case PROSPECT_INFO_COLUMNS.PROSPECT_TITLE:
      case PROSPECT_INFO_COLUMNS.COMPANY_INDUSTRY:
      case PROSPECT_INFO_COLUMNS.COMPANY_INDUSTRY_2:
      case PROSPECT_INFO_COLUMNS.COMPANY_INDUSTRY_3:
      case PROSPECT_INFO_COLUMNS.MARKET:
      case PROSPECT_INFO_COLUMNS.LEAD_TYPE:
      case PROSPECT_INFO_COLUMNS.PROSPECT_LIST:
      case PROSPECT_INFO_COLUMNS.PROSPECT_LIST_2:
      case PROSPECT_INFO_COLUMNS.PROSPECT_LIST_3:
        return getProspectChoicesForColumn(column, prospectInfoOptions ?? null);
      case SessionV2Columns.AGENT_ADDRESS:
        return routes.map(route => ({
            label: route.address,
            value: route.address,
            column: { column_name: SessionV2Columns.AGENT_ADDRESS, table: BackendTables.SESSIONS_V2 }
          }));
      default:
        // Check if this is a custom metric column
        if (isCustomMetricColumn(column)) {
          const definition = getCustomMetricDefinition(column)
          if (definition && definition.value_type === CustomMetricValueType.ENUM) {
            let categories: string[] = []
            
            if (definition.value_source === CustomMetricSource.CODE && definition.value_code_str) {
              // Handle custom code categories
              const codeOption = codeOptions.find(c => c.name === definition.value_code_str)
              if (codeOption?.enum_options) {
                categories = codeOption.enum_options
              }
            } else if (definition.value_type_enum_str) {
              // Handle regular enum categories
              categories = definition.value_type_enum_str.split(',').map((c: string) => c.trim())
            }
            
            return categories.map((category, index) => ({
              label: category,
              value: index.toString(),
              column: { column_name: column, table: BackendTables.CUSTOM_METRICS }
            }))
          }
        }
        return [];
    }
  }, [dispositionInfoOptions, prospectInfoOptions, codeOptions, getCustomMetricDefinition, getProspectChoicesForColumn])

  const hasOptions = useMemo(() => (column: BackendColumns): boolean => {
    // Check if this is a custom metric column
    if (isCustomMetricColumn(column)) {
      const definition = getCustomMetricDefinition(column)
      return definition?.value_type === CustomMetricValueType.ENUM
    }
    
    return getColumnOptions(column).length > 0
  }, [getColumnOptions, getCustomMetricDefinition])

  return {
    getColumnOptions,
    hasOptions
  };
}; 